import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import type { RoleEnumKey } from '@jobilla/nexus-sdk'
import { roleEnum } from '@jobilla/nexus-sdk'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { Button, LargeTextField, SelectField, WidgetLabel, WidgetSelectDropdown } from '../../../../rapid-ui'

interface OrganizationInviteDialogProps {
	isOpen: boolean
	inviteEmails: string
	inviteRole: RoleEnumKey
	invitationErrors: Record<string, string>
	onClose: () => void
	onEmailsChange: (value: string) => void
	onRoleChange: (value: RoleEnumKey) => void
	onSendInvites: () => void
	isJobillan?: boolean
}

export function OrganizationInviteDialog({
	isOpen,
	inviteEmails,
	inviteRole,
	invitationErrors,
	onClose,
	onEmailsChange,
	onRoleChange,
	onSendInvites,
	isJobillan = false,
}: OrganizationInviteDialogProps) {
	const { t } = useTranslation('common')
	return (
		<AnimatePresence mode="popLayout">
			{isOpen && (
				<Dialog
					static={true}
					open={isOpen}
					onClose={onClose}
					className="relative z-50"
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/20 backdrop-blur-sm"
					/>
					<div className="fixed inset-0 w-screen overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4">
							<DialogPanel
								as={motion.div}
								initial={{
									opacity: 0,
									scale: 0.95,
									transition: { duration: 0.2 },
								}}
								animate={{
									opacity: 1,
									scale: 1,
									transition: { duration: 0.2 },
								}}
								exit={{
									opacity: 0,
									scale: 0.95,
									transition: { duration: 0.2 },
								}}
								className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-lg ring-1 ring-grey-200/50"
							>
								<DialogTitle className="text-lg font-medium text-grey-900 mb-2">
									{t('organization.inviteDialog.title')}
								</DialogTitle>
								<p className="text-sm text-grey-700 mb-4">
									{t('organization.inviteDialog.description')}
								</p>
								<div className="space-y-4 mb-6">
									<LargeTextField
										label={t('organization.inviteDialog.emailLabel')}
										value={inviteEmails}
										onChange={onEmailsChange}
										hint={t('organization.inviteDialog.emailHint')}
									/>

									{Object.keys(invitationErrors).length > 0 && (
										<div className="rounded-md bg-red-50 border border-red-200 p-4">
											<div className="flex items-start">
												<div className="flex-shrink-0">
													<svg
														className="h-5 w-5 text-red-400"
														viewBox="0 0 20 20"
														fill="currentColor"
													>
														<path
															fillRule="evenodd"
															d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
															clipRule="evenodd"
														/>
													</svg>
												</div>
												<div className="ml-3 flex-1">
													<h3 className="text-sm font-medium text-red-800 mb-2">
														{t('organization.inviteDialog.someInvitesFailed')}
													</h3>
													<div className="text-sm text-red-700 space-y-1">
														{Object.entries(invitationErrors).map(([email, error]) => (
															<div key={email} className="flex items-start gap-2">
																<span className="font-medium">{email}:</span>
																<span>{error}</span>
															</div>
														))}
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
								<div className={'flex justify-between items-end'}>
									<div>
										<WidgetLabel label={t('organization.inviteDialog.assignRole')}>
											<WidgetSelectDropdown
												label={t('organization.inviteDialog.roleLabel')}
												options={[
													{
														label: t('organization.inviteDialog.roleAdmin'),
														id: roleEnum.admin,
													},
													{
														label: t('organization.inviteDialog.roleMember'),
														id: roleEnum.member,
													},
													{
														label: t('organization.inviteDialog.roleRecruiter'),
														id: roleEnum.recruiter,
													},
													...(isJobillan
														? [{
															label: t('organization.inviteDialog.roleJobillan'),
															id: roleEnum.jobillan,
														}]
														: []),
												]}
												value={inviteRole}
												onChange={onRoleChange}
											/>
										</WidgetLabel>
									</div>
									<div className="flex gap-3 justify-end">
										<Button type="subtle" size="short" onClick={onClose}>
											{t('common.cancel')}
										</Button>
										<Button
											type="colorful"
											size="short"
											onClick={onSendInvites}
											disabled={!inviteEmails.trim()}
										>
											{t('organization.inviteDialog.sendInvites')}
										</Button>
									</div>
								</div>
							</DialogPanel>
						</div>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
